import { ID } from '@griply/types';
import React, { useRef } from 'react';
import { ExternalEventContextMenu } from 'shared/components/connected/external-event-context-menu';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';
import { useOpenExternalEventPopup } from 'shared/hooks/use-open-external-event-popup';

import * as Styled from './event.style';
import { EventCard } from './event-card';
import { useTimeBasedStyle } from './use-time-based-style';

export type ExternalEventProps = {
  externalId: ID;
  name: string;
  url?: string;
  color?: string;
  // absent on responses from an older backend — falls back to opening the url
  connectionId?: ID;
  calendarId?: ID;
  isRecurring?: boolean;
  allDay?: boolean;
  startDate?: string;
  endDate?: string;
  timeText?: string;
  isPast?: boolean;
};

export const ExternalEvent: React.FC<ExternalEventProps> = ({
  externalId,
  name,
  url,
  color,
  connectionId,
  calendarId,
  isRecurring,
  allDay,
  startDate,
  endDate,
  timeText,
  isPast,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { numberOfLines } = useTimeBasedStyle(startDate, endDate);
  const { eventTarget, onOpen: onEdit } = useOpenExternalEventPopup(
    containerRef,
    {
      externalId,
      title: name,
      allDay: !!allDay,
      start: startDate,
      end: endDate,
      connectionId,
      calendarId,
      url,
    },
  );

  const {
    open: openMenu,
    close: closeMenu,
    position: menuPosition,
  } = useContextMenuState({ disabled: !eventTarget });

  const onOpenUrl = () => window.open(url, '_blank', 'noreferrer');

  const _onClick = eventTarget ? onEdit : url ? onOpenUrl : undefined;

  return (
    <>
      <EventCard
        ref={containerRef}
        title={name}
        color={color}
        timeText={timeText}
        isPast={isPast}
        numberOfLines={numberOfLines}
        onClick={_onClick}
        onContextMenu={openMenu}
        leading={<Styled.ColorBarSlot $color={color} $past={isPast} />}
      />
      {!!menuPosition && !!eventTarget && (
        <ExternalEventContextMenu
          eventTarget={eventTarget}
          isRecurring={!!isRecurring}
          location={menuPosition}
          onEdit={onEdit}
          onClose={closeMenu}
        />
      )}
    </>
  );
};
